import {
  hasNoWritePermission,
  openFormulaPopover,
  openHideColumnsPopover,
} from '@/helpers/spreadsheetHelper';
import {
  DELETE_HIDDEN_COLUMNS,
  DELETE_VISIBLE_COLUMNS,
  LINK_DATA_OVERVIEW,
  LINK_FRESHDESC_SUPPORT,
  LINK_GIGASHEET_YOUTUBE_PLAYLIST,
  MODAL_AUTOMATION_API,
  MODAL_CALC_LENGTH,
  MODAL_CHANGE_CASE,
  MODAL_CHANGE_DATA_TYPE,
  MODAL_CLEANUP_COMPANY_NAME,
  MODAL_CLEANUP_UNIX_TIME,
  MODAL_COMBINED_COLUMNS,
  MODAL_CROSS_FILE_VLOOKUP,
  MODAL_CURRENCY,
  MODAL_DATA_ENRICHMENT,
  MODAL_DECODE_BASE64,
  MODAL_EXPLODE_DATE,
  MODAL_EXPLODE_JSON,
  MODAL_EXTRACT_DOMAIN_NAME,
  MODAL_FILTER_PANEL,
  MODAL_FIND_AND_REPLACE,
  MODAL_GROUP_BY,
  MODAL_IF_CONDITIONS,
  MODAL_MOVE_FILE,
  MODAL_PUBLISH_TO_WEB,
  MODAL_RENAME_COLUMNS,
  MODAL_SORT_COLUMN,
  MODAL_SPLIT_COLUMN,
  MODAL_TRIM_WHITESPACE,
  MODAL_URL_FOR_BI_AND_SPREADSHEETS,
} from '@/Utils/constants';

import domoIcon from '@/Assets/icons/Spreadsheets_Icons/domo.svg';
import exelIcon from '@/Assets/icons/Spreadsheets_Icons/exel.svg';
import googleSheetsIcon from '@/Assets/icons/Spreadsheets_Icons/google_sheets.svg';
import lookerIcon from '@/Assets/icons/Spreadsheets_Icons/looker.svg';
import powerBiIcon from '@/Assets/icons/Spreadsheets_Icons/power_bi.svg';
import tableauIcon from '@/Assets/icons/Spreadsheets_Icons/tableau.svg';

export const getMenuItems = (
  openModal,
  firstRowAsHeader,
  headerAsFirstRow,
  openExportFileModal,
  openSharePopup,
  openMakeCopyFileModal,
  openRenameDropdown,
  openDeleteRowModal,
  openDeleteColumnsPopup,
  openDeleteDuplicatesModal,
  disableDelete,
  shouldDisableActions,
  canSplitSheet,
  isAnonymous,
  limitedAnalystMenu,
  isFileShared,
  currentFile,
  insertNewColumn,
  createAndOpenNewBlankSheet,
  openSplitToMultipleCVSsPopup,
  openSheetDetails,
  copySelectedRangeToClipboard,
  pastFromClipboard,
  isCopySelectedRangeToClipboardDisabled,
  onTextWrap,
  rowCount,
  addRowAbove,
  addRowBelow,
  onMenuTitleClickAction,
  canViewSheetHistory,
  sheetHistory,
  handleConnectorExport,
  canAccessBusinessConnectors,
  canAccessEnterpriseConnectors,
  autoSizeAll,
  refreshAllCalculations,
  user,
  enableChatSupport,
  isSnowflakeFile,
  isLiveQuery,
  canShareLiveQuery,
  hubspotAccess,
  isPreviewMode
) => {
  // hide snowflake connector if analyst mode
  const saveToSnowflakeConnectorHidden = limitedAnalystMenu;
  // hide non-snowflake, non-hubspot connectors if analyst mode or snowflake file
  const saveToOtherConnectorsHidden = isSnowflakeFile || limitedAnalystMenu;
  // hide hubspot connector if analyst mode and no hubspot access or snowflake file
  const saveToHubspotConnectorHidden = isSnowflakeFile || (limitedAnalystMenu && !hubspotAccess);
  // hide save to menu if all connectors are hidden or file is shared and no write permission
  // except for the case of hubspot access
  const saveToMenuHidden =
    (isFileShared && hasNoWritePermission(currentFile) && !hubspotAccess) ||
    (saveToSnowflakeConnectorHidden && saveToOtherConnectorsHidden && saveToHubspotConnectorHidden);

  const baseMenuItems = [
    {
      title: 'File',
      hideSubmenu: isAnonymous,
      action: () => {},
      submenu: [
        {
          title: 'New Blank Sheet',
          submenu: [],
          icon: 'file-text',
          action: createAndOpenNewBlankSheet,
          disabled: shouldDisableActions,
          hidden: limitedAnalystMenu,
        },
        {
          title: 'Save Results',
          submenu: [],
          icon: 'floppy-disk',
          action: () => openMakeCopyFileModal(true),
          disabled: shouldDisableActions,
          hidden: isLiveQuery || limitedAnalystMenu,
        },
        {
          title: 'Make a Copy',
          submenu: [],
          icon: 'copy',
          action: openMakeCopyFileModal,
          disabled: shouldDisableActions,
          hidden: limitedAnalystMenu || isLiveQuery,
        },
        {
          title: 'Share',
          submenu: [],
          icon: 'user-plus',
          action: openSharePopup,
          separator: true,
          hidden: isFileShared || limitedAnalystMenu || !canShareLiveQuery,
        },
        {
          title: 'Publish to Web',
          submenu: [],
          icon: 'code',
          action: () => openModal(MODAL_PUBLISH_TO_WEB),
          hidden: isFileShared || limitedAnalystMenu || isLiveQuery,
        },
        {
          title: 'Link Data to',
          hidden: limitedAnalystMenu,
          submenu: limitedAnalystMenu
            ? []
            : [
                {
                  title: 'Excel',
                  submenu: [],
                  iconAsImage: exelIcon,
                  action: () => openModal(MODAL_URL_FOR_BI_AND_SPREADSHEETS),
                  disabled: shouldDisableActions,
                },
                {
                  title: 'Google Sheets',
                  submenu: [],
                  iconAsImage: googleSheetsIcon,
                  action: () => openModal(MODAL_URL_FOR_BI_AND_SPREADSHEETS),
                  disabled: shouldDisableActions,
                },
                {
                  title: 'Looker',
                  submenu: [],
                  iconAsImage: lookerIcon,
                  action: () => openModal(MODAL_URL_FOR_BI_AND_SPREADSHEETS),
                  disabled: shouldDisableActions,
                },
                {
                  title: 'Power BI',
                  submenu: [],
                  iconAsImage: powerBiIcon,
                  action: () => openModal(MODAL_URL_FOR_BI_AND_SPREADSHEETS),
                  disabled: shouldDisableActions,
                },
                {
                  title: 'Domo',
                  submenu: [],
                  iconAsImage: domoIcon,
                  action: () => openModal(MODAL_URL_FOR_BI_AND_SPREADSHEETS),
                  disabled: shouldDisableActions,
                },
                {
                  title: 'Tableau',
                  submenu: [],
                  iconAsImage: tableauIcon,
                  action: () => openModal(MODAL_URL_FOR_BI_AND_SPREADSHEETS),
                  disabled: shouldDisableActions,
                },
                {
                  title: 'Learn More',
                  icon: 'question',
                  submenu: [],
                  action: () => window.open(LINK_DATA_OVERVIEW, '_blank', 'noopener,noreferrer'),
                },
              ],
          icon: 'lightning',
          disabled: shouldDisableActions,
          hideSubmenu: isFileShared && hasNoWritePermission(currentFile),
        },
        {
          title: 'Download',
          submenu: [
            {
              title: 'Export CSV',
              submenu: [],
              action: openExportFileModal,
              disabled: shouldDisableActions,
            },
            {
              title: 'Split to Multiple CSVs',
              submenu: [],
              action: openSplitToMultipleCVSsPopup,
              disabled: shouldDisableActions,
              premiumFeature: isPreviewMode || !canSplitSheet,
            },
          ],
          icon: 'export',
          disabled: shouldDisableActions,
          hideSubmenu: isFileShared && hasNoWritePermission(currentFile),
          hidden: isLiveQuery,
        },
        {
          title: 'Save to',
          submenu: saveToMenuHidden
            ? []
            : [
                {
                  title: 'Databricks',
                  submenu: [],
                  action: handleConnectorExport('databricks', false),
                  disabled: shouldDisableActions,
                  premiumFeature: !canAccessEnterpriseConnectors,
                  hidden: saveToOtherConnectorsHidden,
                },
                {
                  title: 'Snowflake',
                  submenu: [],
                  action: handleConnectorExport('snowflake', false),
                  disabled: shouldDisableActions,
                  premiumFeature: !canAccessEnterpriseConnectors,
                  hidden: saveToSnowflakeConnectorHidden || (isLiveQuery && isSnowflakeFile),
                },
                {
                  title: 'Snowflake View',
                  submenu: [],
                  action: handleConnectorExport('snowflake', true),
                  disabled: shouldDisableActions,
                  premiumFeature: !canAccessEnterpriseConnectors,
                  hidden: !isSnowflakeFile || saveToSnowflakeConnectorHidden,
                },
                {
                  title: 'Snowflake Table',
                  submenu: [],
                  action: handleConnectorExport('snowflake', false),
                  disabled: shouldDisableActions,
                  premiumFeature: !canAccessEnterpriseConnectors,
                  hidden: !isSnowflakeFile || saveToSnowflakeConnectorHidden,
                },
                {
                  title: 'Microsoft SQL Server',
                  submenu: [],
                  action: handleConnectorExport('mssql', false),
                  disabled: shouldDisableActions,
                  premiumFeature: !canAccessEnterpriseConnectors,
                  hidden: saveToOtherConnectorsHidden,
                },
                {
                  title: 'HubSpot',
                  submenu: [],
                  action: handleConnectorExport('hubspot', false),
                  disabled: shouldDisableActions,
                  premiumFeature: !hubspotAccess,
                  hidden: saveToHubspotConnectorHidden,
                },
              ],
          icon: 'database',
          separator: false,
          hidden: saveToMenuHidden,
        },
        {
          title: 'Rename',
          submenu: [],
          icon: 'pencil-simple',
          action: openRenameDropdown,
          separator: true,
          hidden: (isFileShared && hasNoWritePermission(currentFile)) || limitedAnalystMenu,
        },
        {
          title: 'Move',
          submenu: [],
          icon: 'arrows-out-cardinal',
          action: () =>
            openModal(MODAL_MOVE_FILE, {
              isMovingFromSpreadsheet: true,
              file: currentFile,
              folderName: '',
              folderId: currentFile?.metadata?.ParentDirectory,
            }),
          hidden: isFileShared || limitedAnalystMenu,
          disabled: shouldDisableActions,
          currentFile: currentFile,
        },
        {
          title: 'Sheet Info',
          submenu: [],
          icon: 'lightbulb',
          action: openSheetDetails,
          separator: true,
        },
        {
          title: 'Sheet History',
          submenu: [],
          icon: 'clock-counter-clockwise',
          action: sheetHistory,
          premiumFeature: !canViewSheetHistory,
          hidden: limitedAnalystMenu,
        },
      ],
      disabled: !currentFile,
    },
    {
      title: 'Edit',
      disabled: !currentFile || shouldDisableActions,
      action: onMenuTitleClickAction,
      hidden: isLiveQuery,
      hideSubmenu:
        (!isAnonymous && isFileShared && hasNoWritePermission(currentFile)) || isAnonymous,
      submenu: [
        {
          title: 'Copy',
          icon: 'copy',
          action: copySelectedRangeToClipboard,
          disabled: isCopySelectedRangeToClipboardDisabled,
        },
        {
          title: 'Paste',
          icon: 'clipboard-text',
          action: pastFromClipboard,
        },
        {
          title: 'Find & Replace',
          icon: 'magnifying-glass',
          action: () => openModal(MODAL_FIND_AND_REPLACE, true),
          separator: true,
        },
        {
          title: 'Delete',
          separator: true,
          submenu: [
            {
              title: 'Hidden Columns',
              submenu: [],
              action: () => openDeleteColumnsPopup(DELETE_HIDDEN_COLUMNS),
            },
            {
              title: 'Visible Columns',
              submenu: [],
              action: () => openDeleteColumnsPopup(DELETE_VISIBLE_COLUMNS),
            },
            {
              title: 'Row',
              submenu: [],
              action: () => openDeleteRowModal(),
              disabled: disableDelete.singleRow,
              separator: true,
            },
            {
              title: 'Matching Rows',
              submenu: [],
              action: () => openDeleteRowModal('-matching-filter'),
              disabled: disableDelete.filteredRows,
            },
            {
              title: 'Excluded Rows',
              submenu: [],
              action: () => openDeleteRowModal('-not-matching-filter'),
              disabled: disableDelete.unfilteredRows,
            },
          ],
          icon: 'trash',
        },
      ],
    },
    {
      title: 'Insert',
      disabled: !currentFile || shouldDisableActions,
      action: onMenuTitleClickAction,
      hidden: isLiveQuery && !isSnowflakeFile,
      hideSubmenu:
        (!isAnonymous && isFileShared && hasNoWritePermission(currentFile)) || isAnonymous,
      submenu: [
        {
          title: 'New Column',
          submenu: [],
          icon: 'columns',
          hidden: isSnowflakeFile,
          action: () => insertNewColumn(),
        },
        {
          title: rowCount > 1 ? 'New Rows' : 'New Row',
          hidden: isSnowflakeFile,
          submenu: isSnowflakeFile
            ? []
            : [
                {
                  title: `Insert ${rowCount || ''} ${rowCount > 1 ? 'rows' : 'row'} above`,
                  action: addRowAbove,
                },
                {
                  title: `Insert ${rowCount || ''} ${rowCount > 1 ? 'rows' : 'row'} below`,
                  action: addRowBelow,
                  disabled: !rowCount,
                },
              ],
          icon: 'rows',
        },
        {
          title: 'Formula',
          submenu: [
            {
              title: 'Refresh All Calculations',
              action: refreshAllCalculations,
            },
          ],
          icon: 'function',
          action: () => openFormulaPopover({ mode: 'formula' }),
          separator: true,
        },
        {
          title: 'If Then',
          hidden: isSnowflakeFile,
          submenu: [],
          icon: 'list-checks',
          action: () => openModal(MODAL_IF_CONDITIONS, true),
        },
        {
          title: 'Cross File VLookup',
          hidden: isSnowflakeFile,
          submenu: [],
          icon: 'file-search',
          action: () => openModal(MODAL_CROSS_FILE_VLOOKUP, true),
        },
        {
          title: 'Enrich Data',
          hidden: isSnowflakeFile,
          submenu: [],
          icon: 'atom',
          action: () => openModal(MODAL_DATA_ENRICHMENT, true),
        },
        {
          title: 'Character Count',
          hidden: isSnowflakeFile,
          submenu: [],
          icon: 'hash',
          action: () => openModal(MODAL_CALC_LENGTH, true),
          separator: true,
        },
        {
          title: 'Explode Date',
          hidden: isSnowflakeFile,
          submenu: [],
          icon: 'calendar-blank',
          action: () => openModal(MODAL_EXPLODE_DATE, true),
        },
        {
          title: 'Explode Json',
          hidden: isSnowflakeFile,
          submenu: [],
          icon: 'brackets-curly',
          action: () => openModal(MODAL_EXPLODE_JSON, true),
        },
      ],
    },
    {
      title: 'Format',
      disabled: !currentFile || shouldDisableActions,
      action: onMenuTitleClickAction,
      hideSubmenu:
        (!isAnonymous && isFileShared && hasNoWritePermission(currentFile)) || isAnonymous,
      submenu: [
        {
          title: 'Autosize All Columns',
          icon: 'arrows-horizontal',
          action: () => autoSizeAll(),
        },
        {
          title: 'Change Case',
          icon: 'text-aa',
          action: () => openModal(MODAL_CHANGE_CASE, true),
          hidden: isLiveQuery,
        },
        {
          title: 'Currency',
          submenu: [],
          icon: 'currency-dollar-simple',
          action: () => openModal(MODAL_CURRENCY, true),
          hidden: isLiveQuery,
        },
        {
          title: 'Trim Whitespace',
          submenu: [],
          icon: 'scissors',
          action: () => openModal(MODAL_TRIM_WHITESPACE, true),
          hidden: isLiveQuery,
        },
        {
          title: 'Wrap Text',
          submenu: [],
          icon: 'arrow-u-down-left',
          action: () => onTextWrap(MODAL_TRIM_WHITESPACE, true),
        },
        {
          title: 'Header Rows',
          hidden: isLiveQuery,
          submenu: [
            {
              title: 'Set First Row as Header',
              submenu: [],
              action: firstRowAsHeader,
            },
            {
              title: 'Set Header as First Row',
              submenu: [],
              action: headerAsFirstRow,
            },
          ],
          icon: 'table',
        },
      ],
    },
    {
      title: 'Data',
      disabled: !currentFile || shouldDisableActions,
      action: onMenuTitleClickAction,
      hidden: limitedAnalystMenu || isLiveQuery,
      hideSubmenu:
        (!isAnonymous && isFileShared && hasNoWritePermission(currentFile)) || isAnonymous,
      submenu: [
        {
          title: 'Sort',
          icon: 'arrows-down-up',
          submenu: [],
          action: () => openModal(MODAL_SORT_COLUMN, true),
        },
        {
          title: 'Filter',
          submenu: [],
          icon: 'filter',
          action: () => openModal(MODAL_FILTER_PANEL, true),
        },
        {
          title: 'Group',
          submenu: [],
          icon: 'group',
          action: () => openModal(MODAL_GROUP_BY, true),
        },
        {
          title: 'Manage Columns',
          submenu: [],
          icon: 'eye',
          action: () => openHideColumnsPopover(),
        },
        {
          title: 'Change Data Type',
          submenu: [],
          icon: 'swap',
          action: () => openModal(MODAL_CHANGE_DATA_TYPE, true),
          separator: true,
          hidden: limitedAnalystMenu,
        },
        {
          title: 'Split Column',
          icon: 'arrows-split',
          submenu: [],
          action: () => openModal(MODAL_SPLIT_COLUMN, true),
          hidden: limitedAnalystMenu,
        },
        {
          title: 'Combine Columns',
          submenu: [],
          icon: 'arrows-merge',
          action: () => openModal(MODAL_COMBINED_COLUMNS, true),
          hidden: limitedAnalystMenu,
        },
        {
          title: 'Rename Columns',
          submenu: [],
          icon: 'textbox',
          action: () => openModal(MODAL_RENAME_COLUMNS, true),
          hidden: limitedAnalystMenu,
        },
        {
          title: 'Data Cleanup',
          submenu: limitedAnalystMenu
            ? []
            : [
                {
                  title: 'Remove Duplicates',
                  submenu: [],
                  action: () => openDeleteDuplicatesModal(),
                },
                {
                  title: 'Cleanup Company Name',
                  submenu: [],
                  action: () => openModal(MODAL_CLEANUP_COMPANY_NAME, true),
                },
                {
                  title: 'Extract Domain',
                  submenu: [],
                  action: () => openModal(MODAL_EXTRACT_DOMAIN_NAME, true),
                },
                {
                  title: 'Cleanup Unix Time',
                  submenu: [],
                  action: () => openModal(MODAL_CLEANUP_UNIX_TIME, true),
                },
                {
                  title: 'Decode Base64 Content',
                  submenu: [],
                  action: () => openModal(MODAL_DECODE_BASE64, true),
                },
              ],
          icon: 'broom',
          hidden: limitedAnalystMenu,
        },
      ],
    },
    {
      title: 'Automation',
      disabled: !currentFile || shouldDisableActions,
      action: () => {
        onMenuTitleClickAction() && openModal(MODAL_AUTOMATION_API, true);
      },
      submenu: [],
      hideSubmenu: true,
      tooltip: 'Like Macros, but doesn’t suck',
      hidden: limitedAnalystMenu,
    },
    {
      title: 'Help',
      disabled: !currentFile || shouldDisableActions,
      action: () => {},
      hideSubmenu: isAnonymous,
      submenu: [
        {
          title: 'Chat',
          icon: 'chat-teardrop-text',
          submenu: [],
          hidden: !enableChatSupport,
          action: () => {
            if (user?.is_anonymous) {
              window.open('https://gigasheet.com/', '_blank');
              return;
            }
            window.fcWidget.open();
          },
        },
        {
          title: 'Support',
          icon: 'question',
          submenu: [],
          action: () => window.open(LINK_FRESHDESC_SUPPORT, '_blank', 'noopener,noreferrer'),
        },
        {
          title: 'What’s New',
          icon: 'gift',
          submenu: [],
          action: () => {
            if (user?.is_anonymous) {
              window.open('https://www.gigasheet.com/features/features', '_blank');
              return;
            }
            window.userGuiding?.previewGuide(98580);
          },
        },
        {
          title: 'Video Tutorials',
          icon: 'video',
          submenu: [],
          action: () =>
            window.open(LINK_GIGASHEET_YOUTUBE_PLAYLIST, '_blank', 'noopener,noreferrer'),
        },
      ],
    },
  ];

  if (limitedAnalystMenu) {
    const TITLES_TO_EXCLUDE = ['Edit', 'Insert', 'Format', 'Automation'];
    return baseMenuItems.filter((item) => !TITLES_TO_EXCLUDE.includes(item.title));
  }

  return baseMenuItems;
};
